import React from 'react';
import Navbar from '../Homepage/Navbar';
import Footer from '../Homepage/Footer';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faGraduationCap, faLightbulb, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const SchemeCard = ({ title, icon, points, delay, color, link }) => {
    const colorClasses = {
        blue: {
            border: "hover:border-blue-500/50",
            shadow: "hover:shadow-blue-500/20",
            bg: "bg-blue-500/10",
            bgHover: "group-hover:bg-blue-500/20",
            text: "text-blue-400",
            dot: "bg-blue-500",
            blob: "bg-blue-500/10"
        },
        purple: {
            border: "hover:border-purple-500/50",
            shadow: "hover:shadow-purple-500/20",
            bg: "bg-purple-500/10",
            bgHover: "group-hover:bg-purple-500/20",
            text: "text-purple-400",
            dot: "bg-purple-500",
            blob: "bg-purple-500/10"
        },
        orange: {
            border: "hover:border-orange-500/50",
            shadow: "hover:shadow-orange-500/20",
            bg: "bg-orange-500/10",
            bgHover: "group-hover:bg-orange-500/20",
            text: "text-orange-400",
            dot: "bg-orange-500",
            blob: "bg-orange-500/10"
        }
    };

    const theme = colorClasses[color] || colorClasses.blue;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delay }}
            viewport={{ once: true }}
            className={`bg-dark-tertiary p-8 rounded-2xl shadow-lg border border-white/5 ${theme.border} transition-all duration-300 ${theme.shadow} group relative overflow-hidden`}
        >
            <div className={`absolute top-0 right-0 w-32 h-32 ${theme.blob} rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none ${theme.bgHover} transition-all duration-500`}></div>

            <div className={`w-16 h-16 ${theme.bg} rounded-xl flex items-center justify-center mb-6 ${theme.text} group-hover:scale-110 transition-transform duration-300`}>
                <FontAwesomeIcon icon={icon} className="text-3xl" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-6 group-hover:text-nvcti-orange transition-colors">{title}</h3>

            <ul className="space-y-4 mb-8">
                {points.map((item, index) => (
                    <li key={index} className="flex items-start text-gray-300">
                        <span className={`mr-3 mt-1.5 w-2 h-2 ${theme.dot} rounded-full flex-shrink-0`}></span>
                        <span className="leading-relaxed text-gray-300">{item}</span>
                    </li>
                ))}
            </ul>

            {link && (
                <Link to={link} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl ${theme.bg} ${theme.text} hover:bg-opacity-20 transition-all duration-300 font-semibold group/btn`}>
                    Know More
                    <FontAwesomeIcon icon={faArrowRight} className="transform group-hover/btn:translate-x-1 transition-transform" />
                </Link>
            )}
        </motion.div>
    );
};

const StudentInnovationSchemes = () => {
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    return (
        <div className="min-h-screen flex flex-col bg-dark-secondary">
            <Navbar />

            <main className="flex-grow pt-24 pb-20">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                        className="text-center max-w-4xl mx-auto mb-20"
                    >
                        <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
                            Student <span className="text-transparent bg-clip-text bg-gradient-to-r from-nvcti-orange to-red-500">Innovation Schemes</span>
                        </h1>
                        <p className="text-xl text-gray-300 leading-relaxed">
                            NVCTI also supports structured student growth through dedicated innovation programs designed to foster creativity and technical expertise.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <SchemeCard
                            title="Minor in Product Development"
                            icon={faGraduationCap}
                            points={[
                                "An academic program where students earn a minor degree by working on real-world products and prototypes.",
                                "Combines design thinking, engineering, and entrepreneurship.",
                                "A monthly stipend of approximately ₹6,000 will be provided for a duration of 12 months."
                            ]}
                            delay={0.1}
                            color="blue"
                            link="/programs/mpdi"
                        />

                        <SchemeCard
                            title="In-House Product Development Internship"
                            icon={faLaptopCode}
                            points={[
                                "Hands-on training within NVCTI to work on ongoing or new hardware/software products.",
                                "Provides mentorship, access to resources, and structured project timelines to convert ideas into viable solutions."
                            ]}
                            delay={0.2}
                            color="purple"
                            link="/programs/ipd"
                        />
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default StudentInnovationSchemes;
